import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { apiUrl } from "@/lib/api";
import { fetchWithAuth, isAuthenticated } from "@/utils/fetchWithAuth";
import { storeAuthTokens, resolvePostLoginPath } from "@/utils/postAuth";
import { getAttributionPayload, metaTrack, metaTrackCustom, gaTrack, signupAudience } from "@/lib/analytics";
import { trackEvent, getAnonymousId } from "@/lib/tracker";
import { getVariant, COMPANY_FLOW_EXPERIMENT, COMPANY_FLOW_ARMS } from "@/lib/experiment";
import { useBranding } from "@/contexts/BrandingContext";
import { SiftMark } from "@/components/SiftWordmark";
import StepProgress from "@/components/StepProgress";
import { useFieldErrors, FieldError } from "@/lib/formErrors";
import { nameFromEmail } from "@/lib/nameFromEmail";
import { companyFromEmail } from "@/lib/companyFromEmail";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import { saveCompanyDraft, loadCompanyDraft, clearCompanyDraft,
  saveSignupProgress, loadSignupProgress, clearSignupProgress, hasSeenPeek, markPeekSeen, PEEK_ENABLED } from "@/lib/needDraft";
import TalentPeek from "@/components/company/TalentPeek";
import { useAutofillAdoption } from "@/lib/autofillAdoption";

/* Company signup, and nothing else.

   The business side used to run the same post-first flow as individuals: write
   a job, get it drafted, fill in location and dates and budget, look at a
   preview dashboard, and only then make an account. That is the right shape for
   an individual — the post IS their presence here, and drafting it from a
   resume saves them work they'd otherwise have to do. A company gets none of
   that. They already know how to write a job post, we save them nothing by
   writing it, and the post is worth little until there is supply to answer it.
   Meanwhile the question they actually came with is "is there anyone here worth
   hiring", which a composer does not answer. Four screens of work in front of
   an unanswered question is how 977 visitors produced 2 accounts.

   So: name, work email, company. The follow-up email carries the "how to use
   this" material, and a person follows up directly. At this volume a founder
   who calls converts better than any funnel, and every screen removed is a
   screen that cannot leak.

   Business type is deliberately not asked. It defaults to "general" server-side
   and seeds matching; the dashboard can collect it later against a real profile
   instead of taxing the one screen standing between a company and an account. */

const validEmail = (v) => /\S+@\S+\.\S+/.test(v || "");

export default function GetStartedCompany({ onSwitchAudience, intent = null, draft = null, onBack = null, step = 0, totalSteps = 0, ask = null }) {
  const navigate = useNavigate();
  const branding = useBranding();

  /* How many people already fit the post they just wrote. Carried from the
     composer; absent for anyone who skipped it or came in another way, and
     zero is a perfectly ordinary answer that the copy below handles. */
  const draftMatchCount = Number(draft?.matchCount) || 0;

  /* Did they already sign up, one tap, on the way in?

     Read once at mount, not on every render: this screen creates the account
     itself in the ordinary case, and re-reading would flip the form's whole
     shape underneath somebody the moment it succeeds. */
  const [alreadySignedIn] = useState(() => {
    try { return isAuthenticated(); } catch { return false; }
  });

  /* Founding places left, read live rather than written into the copy.
     The programme is capped at 100 organisations and the waiver is permanent,
     so the sentence below is true today (13 taken) and would quietly become a
     lie the day the hundredth signs up. Asking the server costs one
     unauthenticated GET and means the claim cannot outlive the offer: no
     answer, or none left, and the strip simply does not render. */
  /* 4%, or 5% when a partner referred them -- the partner's commission comes
     out of the platform fee, so the rate genuinely differs and printing a flat
     4% on a partner's own subdomain would be quoting the wrong number to the
     people most likely to check it. Mirrors Organization.effective_platform_fee_bps. */
  const foundingFeePct = branding?.partner_referral_code ? 5 : 4;

  /* "Email this to yourself." Closed by default and one tap from open, so it
     never competes with the primary action -- it is the thing you reach for
     once you have decided not to press the other one. */
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareState, setShareState] = useState("idle"); // idle | sending | sent | error
  /* Why the rescue is on screen, when it was not them who opened it.
     null = they opened it themselves | "idle" | "returned" */
  const [sharePromptReason, setSharePromptReason] = useState(null);
  /* Why the signup did not go through, in their words, when it did not.
     A refusal used to end the conversation: a red line on a field, or a toast,
     and whatever they had written was ours and their address was not. Saying
     what happened and offering to send it to them turns a dead end into a
     lead we can follow up. */
  const [failReason, setFailReason] = useState("");

  /* Which boxes to light up after somebody presses the button on an empty
     form. Pressing a primary action and having NOTHING happen is its own
     failure -- it reads as a dead button, which is how people leave. The old
     behaviour focused the first field and said nothing, on the reasoning that
     an untouched form is not a mistake to be told off for. That reasoning
     holds; saying nothing at all does not. */
  const [needFields, setNeedFields] = useState([]);
  const clearNeed = (name) =>
    setNeedFields((prev) => (prev.length ? prev.filter((f) => f !== name) : prev));
  const needRing = (name) =>
    needFields.includes(name)
      ? " border-brand ring-2 ring-brand/30"
      : "";

  const shareDraft = async () => {
    const addr = shareEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) { setShareState("error"); return; }
    setShareState("sending");
    try {
      const res = await fetch(apiUrl("/api/accounts/public/company/share-draft/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "omit", mode: "cors",
        body: JSON.stringify({
          email: addr,
          role: draft?.title || "",
          description: draft?.description || "",
          location: draft?.location || "",
          match_count: draftMatchCount,
          anonymous_id: getAnonymousId(),
        }),
      });
      const data = res.ok ? await res.json().catch(() => null) : null;
      if (!data?.sent) { setShareState("error"); return; }
      setShareState("sent");
      // Counted as its own step, not as a signup: this is a person who did
      // NOT create an account, and rolling it into the signup numbers would
      // hide the thing the option exists to measure.
      trackEvent("share_draft_sent", {
        account_type: "organization", had_matches: draftMatchCount > 0,
      });
      gaTrack("share_draft_sent", { account_type: "organization" });
    } catch {
      setShareState("error");
    }
  };

  const [founding, setFounding] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(apiUrl("/api/accounts/public/founding-stats/"), {
          credentials: "omit", mode: "cors", headers: { Accept: "application/json" },
        });
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (alive && Number.isFinite(data?.orgs_left) && Number.isFinite(data?.orgs_taken)) {
          setFounding({ left: data.orgs_left, taken: data.orgs_taken });
        }
      } catch { /* the screen is complete without it */ }
    })();
    return () => { alive = false; };
  }, []);

  /* Shares the talent form's error machinery rather than using bare toasts.
     Two reasons, both learned the hard way: a toast pins to the top of the
     viewport and iOS webviews routinely render it off-screen while the
     keyboard is up, which is exactly when a form fires one — so the button
     reads as dead. And a toast records nothing, so a company refused here
     would have been indistinguishable in the funnel from one who wandered
     off. readField also defends against autofill writing into the element
     without React hearing about it. */
  const {
    errors, fieldProps, fail: failValidation, showFieldError, showFieldErrors,
    clear: clearError, reset: resetErrors, readField, refs: fieldRefs,
  } = useFieldErrors({ account_type: "organization", flow: "company_minimal" });

  /* A partner's referral, carried on their link.

     This was live on the old business form and was silently lost when the
     business audience moved to this screen: no `partner_attribution_applied`
     event has ever been recorded. A referred company that signs up through a
     partner's link is worth a different fee to us and a commission to them,
     so dropping it costs both sides real money and nobody would see it happen.

     Link only, no field. Partners paste all three spellings of the parameter,
     hence all three. The manual-entry path still exists on
     /auth/organization/register for a partner who read a code out loud; it does
     not belong on the one screen standing between a company and an account,
     which already converts badly enough. */
  const [partnerCode] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return (p.get("partner_code") || p.get("ref") || p.get("referral_code") || "").trim();
    } catch {
      return "";
    }
  });

  /* Attribution is its own call, after the account exists: the passwordless
     register endpoint has no idea what a partner code is. It must never take
     the signup down with it — the account is already created by this point, so
     a failure here is a reconciliation job for the partner admin, not
     something to show somebody who just signed up. */
  const applyPartnerCode = async (orgId) => {
    if (!partnerCode || !orgId) return;
    try {
      await fetchWithAuth("/api/partners/attribution/apply/", {
        method: "POST",
        json: { organization_id: String(orgId), referral_code: partnerCode },
      });
      trackEvent("partner_attribution_applied", {
        account_type: "organization", flow: "company_minimal",
      });
    } catch {
      trackEvent("partner_attribution_failed", {
        account_type: "organization", flow: "company_minimal",
      });
    }
  };

  /* Show them who is available before asking who they are.

     Eighteen companies reached this form in a day and fifteen produced no
     interaction at all. They arrive asking "is there anyone here worth
     hiring"; three fields asking for their own details answer a different
     question. TalentPeek shows real availability posts for a few seconds
     first, then hands over here. It skips itself if the board is empty.

     Seeded from sessionStorage, not just component state. Production found two
     ways this screen came back after it had already been seen: a remount (this
     webview reloads freely -- 9 of 43 visitors re-entered /get-started in one
     session, one of them five times), and the audience switch, which mounts
     the other form and showed a visitor a second peek 19 seconds after the
     first. One peek per visit, whichever side they land on. */
  const [peeked, setPeeked] = useState(
    () => hasSeenPeek() || !!loadSignupProgress("business")
  );

  /* What they typed, held across a reload they did not ask for.

     The form this one replaced saved a draft and this one did not, so a reload
     cost a company all three fields and there was nothing to say it had
     happened. Three fields is fifteen seconds of typing, which sounds like
     nothing until you notice where this traffic lives: four fifths of it is
     inside Meta's in-app browser, where an unrequested reload is a normal
     event -- the Android webview renderer is killed under memory pressure and
     the browser quietly reloads. Somebody who has just retyped their details
     once does not retype them twice.

     Audience-scoped by lib/needDraft, so a half-written job post from the
     talent side cannot surface here under labels that would all be wrong.

     A restore also skips TalentPeek, above: somebody who was already filling
     this in has had the pitch, and putting it back in front of them delays the
     screen they were on and lands the "picked up where you left off" toast on
     a screen with nothing to pick up. */
  const [restored] = useState(() => loadSignupProgress("business"));

  const [name, setName] = useState(restored?.name || "");
  const [email, setEmail] = useState(restored?.email || "");
  const [orgName, setOrgName] = useState(restored?.orgName || "");

  /* Same autofill trap as the talent form, and the same fix: React's next
     commit writes its own empty state back over whatever iOS AutoFill just
     put in these boxes. See lib/autofillAdoption. */
  const adoptAutofill = useAutofillAdoption(fieldRefs, { name: setName, email: setEmail, orgName: setOrgName }, []);
  /* All three fields, on arrival.

     This screen used to show only the email and reveal the other two once an
     address was typed. That was a reasonable answer to the old problem -- three
     inputs under "Set up your company" read as a wall -- but the wall is not
     what this screen is any more. It now arrives after the composer, so the
     visitor has already written a job post and is one step from an account, and
     a form that grows while you look at it reads as an unknown amount of work
     left rather than a small known one. Showing everything is the honest
     version of "almost done": three short fields, all visible, no surprises. */

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [pwd, setPwd] = useState("");
  const [pwdSet, setPwdSet] = useState(false);
  const [settingPwd, setSettingPwd] = useState(false);
  // Whether the pre-signup draft actually landed, which decides the destination.
  const [postedDraft, setPostedDraft] = useState(false);
  // Which posting, so "See who matches" can open the matches for THAT post.
  const [postedId, setPostedId] = useState(null);

  useEffect(() => {
    gaTrack("get_started_preview_view", { account_type: "organization" });
    trackEvent("get_started_preview_view", {
      audience: "business", account_type: "organization", flow: "company_minimal",
    });
    trackEvent("signup_started", { account_type: "organization", flow: "company_minimal" });
    // There is no composer and no draft step, so arriving here IS reaching
    // sign-up. Firing it now keeps the dashboard funnel's "Reached sign-up"
    // row meaningful for this side instead of silently reading zero.
    trackEvent("get_started_start", {
      audience: "business", account_type: "organization", flow: "company_minimal",
    });
  }, []);

  /* Ask before they are gone, not after.

     `share_draft_sent` was 0 in thirty days. Not "low": zero, never once. The
     option was real and the copy was right, and it sat at the bottom of a
     screen this audience leaves in a median of 25 seconds, half of them inside
     30. Something you have to scroll to and decide to want cannot be reached in
     25 seconds by somebody who has already decided to go.

     So it comes to them, on the two signals that actually precede leaving:

       idle     -- 25 seconds without a keystroke or a tap, which is the median
                   dwell, so this fires at roughly the moment the median
                   visitor stops being a visitor.
       returned -- the tab went away and came back. On a phone inside Meta's
                   in-app browser, which is most of this traffic, that is what
                   leaving looks like; there is no exit-intent cursor to read
                   and `pagehide` is too late to render anything.

     Only ever with a draft in hand: "email this to you" needs an `it`, and the
     one thing these people have already made is the post. It opens the panel
     they would have had to find, and never sends anything on its own. */
  const promptedRef = useRef(false);
  const idleTimerRef = useRef(null);
  const shareRef = useRef(null);
  const hasDraft = !!draft?.title;

  const openSharePrompt = (reason) => {
    if (promptedRef.current) return;
    promptedRef.current = true;
    setShareOpen(true);
    setSharePromptReason(reason);
    trackEvent("share_draft_prompted", {
      account_type: "organization", flow: "company_minimal",
      reason, had_matches: draftMatchCount > 0, match_count: draftMatchCount,
    });
  };

  useEffect(() => {
    // Nothing to rescue, or nothing left to rescue them from.
    if (!hasDraft || done || shareState === "sent" || promptedRef.current) return undefined;

    const IDLE_MS = 25000;
    const arm = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => openSharePrompt("idle"), IDLE_MS);
    };
    /* Typing in the form is the opposite of abandoning it: a prompt that
       interrupts somebody mid-address is the version of this that loses
       accounts instead of saving drafts. */
    const bump = () => { if (!promptedRef.current) arm(); };

    /* A real departure, not a flicker.

       An autofill sheet, a password manager, a permissions dialog and the
       keyboard itself all flip visibility for a moment on iOS, and coming back
       from one of those is somebody still filling the form in. Requiring a few
       seconds away separates "went to find the company card" from "tapped the
       email box". Starting at 0 also means a page that merely loaded in a
       background tab does not count as a return. */
    let hiddenAt = 0;
    const AWAY_MS = 4000;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt >= AWAY_MS) openSharePrompt("returned");
      hiddenAt = 0;
    };

    arm();
    const opts = { passive: true };
    window.addEventListener("keydown", bump, opts);
    window.addEventListener("pointerdown", bump, opts);
    window.addEventListener("input", bump, opts);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      window.removeEventListener("keydown", bump, opts);
      window.removeEventListener("pointerdown", bump, opts);
      window.removeEventListener("input", bump, opts);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDraft, done, shareState]);

  /* Bring it into view, or it has not actually been offered.

     On a phone this block sits below three fields, two social buttons and the
     founding strip, which is exactly why nobody ever found it. Opening it
     off-screen would reproduce the original bug with more code. Smooth, and
     only when the prompt opened itself -- somebody who tapped the link is
     already looking at it. */
  useEffect(() => {
    if (!sharePromptReason || !shareRef.current) return;
    try {
      shareRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {
      /* older webviews: the panel is open either way */
    }
  }, [sharePromptReason]);

  /* Say it survived, and count it.

     Restoring silently is barely better than not restoring: somebody who
     believes their work is gone has already decided to leave by the time they
     look at the fields. And without the event a recovery is invisible in the
     funnel, indistinguishable from the abandonment it just prevented, so there
     would be no way to tell whether any of this earns its keep. */
  useEffect(() => {
    if (!restored) return;
    toast.success("Picked up where you left off.");
    trackEvent("signup_progress_restored", {
      account_type: "organization", flow: "company_minimal",
    });
  }, [restored]);

  /* Debounced, because serialising on every keystroke is a synchronous
     localStorage write on exactly the slow phones this exists to help, and
     typing lag would cost more people than the crash does. A third of a second
     sits well inside the gap between the last keystroke and the tap that moves
     them on.

     Stops once the account exists: from then on the work is on the server and
     all that is left in here is somebody's email address on their phone. */
  useEffect(() => {
    if (done) return undefined;
    const id = setTimeout(() => {
      saveSignupProgress({ audience: "business", name, email, orgName });
    }, 300);
    return () => clearTimeout(id);
  }, [done, name, email, orgName]);

  const firstName = name.trim().split(/\s+/)[0] || "";

  // A work email and a company name are what registration actually needs here.
  // The person's name is derived from the address when it is blank.
  // Email only now (2026-10-04): the name and the company are worked out from
  // the address, and the company says who it really is on its home screen,
  // which a contract can't go out without. A one-tap signup has no address to
  // work from, so it is still asked for the company.
  /* The company_signup_flow split (lib/experiment.js). `current` is
     production's three boxes, where a blank name or company is still worked
     out from the email; `new` is the email box alone. Not assigned to a
     one-tap signup, which only ever sees the company box. */
  const [flowArm] = useState(() => (alreadySignedIn ? null
    : getVariant(COMPANY_FLOW_EXPERIMENT, COMPANY_FLOW_ARMS, { primary: false })));
  const fullForm = flowArm === "current";
  useEffect(() => {
    if (!flowArm) return;
    trackEvent("company_flow_shown", { arm: flowArm, door: "company_doors" });
    trackEvent("company_flow_account", { arm: flowArm, door: "company_doors" });
  }, [flowArm]);
  const formReady = alreadySignedIn ? !!orgName.trim()
    : fullForm ? validEmail(email.trim()) && !!orgName.trim() : validEmail(email.trim());

  /* They already have an account, so the last step is the COMPANY.
     One tap on the landing page creates a person and nothing else -- OAuth
     never returns an organisation -- and the old ending to this flow tried to
     register them again, which is a 409 and a dead end for somebody who has
     just signed up. This branch asks the one thing still missing, creates the
     organisation, and hangs their drafted posting on it.

     /api/accounts/organizations/ is the same endpoint the org-register screen
     uses: it creates the org, makes them Owner, sets it active, and assigns
     the founding place. */
  const createOrgForSignedInUser = async (orgVal) => {
    setSubmitting(true);
    trackEvent("org_created_after_oauth", { account_type: "organization", flow: "company_doors" });
    try {
      /* They may already HAVE a company: a one-tap signup creates one at
         registration with a derived name, precisely so the posting they
         drafted has somewhere to live. This screen is then them naming it, not
         founding a second one -- creating another would leave the account
         owning two organisations and the posting attached to the wrong one. */
      let orgId = null;
      try {
        const me = await fetchWithAuth("/api/accounts/me/");
        orgId = me?.organization?.id || null;
      } catch { /* fall through and create one */ }

      if (orgId) {
        await fetchWithAuth(`/api/accounts/organizations/${orgId}/`, {
          method: "PATCH",
          json: { name: orgVal },
        });
      } else {
        const org = await fetchWithAuth("/api/accounts/organizations/", {
          method: "POST",
          json: { name: orgVal },
        });
        orgId = org?.id;
      }
      if (!orgId) throw new Error("no org id");
      await applyPartnerCode(orgId);
      gaTrack("sign_up", { method: "oauth_org", account_type: "organization" });
      trackEvent("account_created", {
        method: "oauth_org", account_type: "organization", flow: "company_doors",
      });

      if (draft?.title) {
        try {
          await fetchWithAuth("/api/accounts/postings/", {
            method: "POST",
            json: {
              poster_type: "org",
              organization_id: String(orgId),
              title: draft.title.slice(0, 255),
              description: draft.description || "",
              location: draft.location || "",
              // Saved, not published: they publish from their home screen.
              status: "draft",
            },
          });
          setPostedDraft(true);
          gaTrack("job_posting_created", { poster_type: "org" });
          metaTrackCustom("JobPosted", signupAudience("organization"));
          trackEvent("first_posting_created", {
            poster_type: "org", account_type: "organization", flow: "company_doors", count: 1,
          });
        } catch {
          trackEvent("posting_create_failed", { account_type: "organization", flow: "company_doors" });
          toast("Company created. Give your post one last look and save it.", { icon: "📝" });
        }
      }
      clearSignupProgress();
      clearCompanyDraft();
      toast.success("You're all set.");
      await goDashboard();
    } catch (e) {
      trackEvent("org_create_failed", { account_type: "organization", flow: "company_doors" });
      showFieldError("orgName", e?.data?.name?.[0] || "Could not create the company. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const createAccount = async () => {
    // See the talent form: take the browser's fill before reading.
    adoptAutofill();
    const nameVal = readField("name", name);
    const emailVal = readField("email", email);
    const orgVal = readField("orgName", orgName);
    if (nameVal !== name) setName(nameVal);
    if (emailVal !== email) setEmail(emailVal);
    if (orgVal !== orgName) setOrgName(orgVal);

    /* A blank name is not a reason to refuse somebody an account.

       Two of the thirteen signup blocks in the first instrumented day landed
       on this field, on this form, and neither visitor came back. The name
       greets them on the dashboard and it can be changed in a text box; the
       email and the company are what registration actually needs. Derive the
       name from the address and carry on. See lib/nameFromEmail. */
    let resolvedName = nameVal;
    if (!resolvedName && validEmail(emailVal)) {
      resolvedName = nameFromEmail(emailVal);
      trackEvent("signup_name_derived", {
        account_type: "organization", flow: "company_minimal", got: !!resolvedName,
      });
    }
    /* An untouched form is not a mistake. Two visitors on 2026-08-13 tapped
       this button with nothing filled in -- one of them three seconds after the
       page loaded, which is not enough time to type anything. They were
       treating the primary button as "continue", which is exactly what the CTA
       they had just pressed was. Answering that with "That needs an @ in it"
       corrects a mistake they never made.

       So when the whole form is empty, put the cursor in the first field and
       say nothing. It is recorded under its own name rather than as a
       validation block, because it is a different thing entirely and averaging
       the two hides both. */
    if (!nameVal && !emailVal && !orgVal) {
      trackEvent("signup_empty_submit", {
        account_type: "organization", flow: "company_minimal",
      });
      // Ordinary validation, which is what people expect a form to do when
      // they press the button and nothing is filled in: say the fields are
      // required, mark them, and put the cursor in the first one.
      if (fullForm) {
        setNeedFields(["email", "name", "orgName"]);
        showFieldErrors({
          email: "Please enter your email address.",
          name: "Please enter your name.",
          orgName: "Please enter your company name.",
        });
      } else {
        setNeedFields(["email"]);
        showFieldErrors({ email: "Please enter your email address." });
      }
      focusNext("gc-email");
      return undefined;
    }

    /* A blank company is not a reason to refuse somebody the account.

       The same argument nameFromEmail already won on this form, and the
       evidence is a business: 2026-09-15 07:47, a company that typed a need,
       waited 41 seconds for the draft, read its matches, filled the other
       fields, pressed Sign up with Company empty and never came back. The
       event says ever_focused: false -- they never clicked the box. Across
       the whole company side, 15 visitors have been refused here and NOT ONE
       of them ever came back and created an account.

       The company name is a text box on the next screen. It is not identity:
       Organization.name is deliberately not unique, slug carries that and
       disambiguates itself, and person-named organisations already exist on
       the board. So derive it -- from a company email domain first, then from
       their own name -- and only refuse when there is neither.

       Computed BEFORE the signed-in branch so both endings get it. */
    let resolvedOrg = orgVal;
    if (!resolvedOrg) {
      const fromDomain = companyFromEmail(emailVal);
      resolvedOrg = fromDomain || resolvedName || "";
      if (resolvedOrg) {
        trackEvent("signup_org_derived", {
          account_type: "organization", flow: "company_minimal",
          from: fromDomain ? "email_domain" : "person_name",
        });
      }
    }

    /* Signed in already: the only thing still missing is the company.

       Checked before the email rules, because this screen does not ask
       somebody for an email they have already handed over with one tap --
       which also means there is no address here to derive a company from, so
       a blank one has to be asked for rather than guessed. Refusing costs
       little at this point: the account already exists and they can be
       followed up. */
    if (alreadySignedIn) {
      if (!resolvedOrg) return failValidation("orgName", "What's the company called?");
      return createOrgForSignedInUser(resolvedOrg);
    }

    if (!validEmail(emailVal)) {
      /* Name the actual problem. "Please enter a valid work email" tells
         somebody staring at what they believe is their address precisely
         nothing, and this exact refusal fired twice in a row for one visitor
         on 2026-08-13 with eleven characters in the box before they gave up.

         The metadata records the SHAPE of what they typed, never the value:
         whether it had an @ and whether anything followed the dot. That is the
         difference between "they typed a phone number" and "our rule is wrong",
         and it costs nothing to know. */
      const shape = {
        has_at: emailVal.includes("@"),
        has_dot: emailVal.includes("."),
        dot_after_at: /@[^@]*\./.test(emailVal),
        has_space: /\s/.test(emailVal),
      };
      const message = !shape.has_at
        ? "That needs an @ in it, like you@company.com."
        : !shape.dot_after_at
          ? "That address is missing its ending, like .com."
          : shape.has_space
            ? "That address has a space in it."
            : "Please check that email address.";
      return failValidation("email", message, shape);
    }

    // Only reachable when there was no company name AND nothing to derive one
    // from, which needs a role mailbox on a free provider.
    if (!resolvedOrg) return failValidation("orgName", "Please enter your company name.");

    resetErrors();
    setSubmitting(true);
    trackEvent("signup_submitted", { account_type: "organization", flow: "company_minimal", door: intent });
    try {
      const res = await fetch(apiUrl("/api/accounts/register_org_passwordless/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: resolvedName,
          first_name: resolvedName,
          email: emailVal,
          org_name: resolvedOrg,
          anonymous_id: getAnonymousId(),
          ...getAttributionPayload(),
        }),
        credentials: "omit",
        mode: "cors",
      });

      if (res.status === 409) {
        toast("You already have an account. Sign in and we'll take you there.", { icon: "👋" });
        return navigate("/auth/login?next=/home");
      }
      if (!res.ok) {
        // Same treatment as the talent form: put the server's complaint on the
        // field it named, and record it under its own name so a rejection we
        // caused is never averaged in with one the visitor caused.
        const d = await res.json().catch(() => ({}));
        const serverField = d.email ? "email" : d.org_name ? "orgName" : "email";
        // See the note on the talent form: a rate limit needs its own words,
        // and every server rejection needs a field to land on rather than a
        // toast the keyboard can hide.
        const message = res.status === 429
          ? "Too many attempts from your network. Please wait a few minutes and try again."
          : (d.email?.[0] || d.org_name?.[0] || d.error
             || "Could not create your account. Please try again.");
        trackEvent("signup_rejected_by_server", {
          account_type: "organization", flow: "company_minimal",
          status: res.status, field: serverField,
        });
        showFieldError(serverField, message);
        // Tell them why, and offer to send them what they wrote.
        setFailReason(message);
        setShareOpen(true);
        if (!shareEmail && emailVal) setShareEmail(emailVal);
        return;
      }

      const data = await res.json();
      storeAuthTokens(data);
      const audience = signupAudience("organization");
      metaTrack("CompleteRegistration", audience);
      metaTrack("StartTrial", audience);
      gaTrack("sign_up", { method: "passwordless", account_type: "organization" });
      trackEvent("account_created", {
        method: "passwordless", account_type: "organization", flow: "company_minimal",
      });
      const orgId = data?.user?.organization?.id || data?.organization?.id;
      await applyPartnerCode(orgId);

      /* If they wrote a post before signing up, save it now.
         The draft is the reason they came through this door, and asking them to
         retype it on the other side of a registration form is how the demand
         funnel lost people in the first place. A failure here is not fatal: the
         account is real, so the draft goes to the composer instead of being
         dropped, and `postedDraft` decides which screen they land on. */
      if (draft?.title && orgId) {
        try {
          /* The id is kept, not thrown away. The button on this screen says
             "See who matches" when people matched, and a promise like that has
             to be paid: landing them on a list of their own postings makes them
             hunt for the four faces they were just shown. Routing to the
             matches drawer needs the posting it belongs to. */
          const madePost = await fetchWithAuth("/api/accounts/postings/", {
            method: "POST",
            json: {
              poster_type: "org",
              organization_id: String(orgId),
              title: draft.title.slice(0, 255),
              description: draft.description || "",
              location: draft.location || "",
              // Saved, not published: they publish from their home screen.
              status: "draft",
            },
          });
          setPostedDraft(true);
          setPostedId(madePost?.id || null);
          gaTrack("job_posting_created", { poster_type: "org" });
          /* The value event, and until now it never reached Meta at all.
             A company that posts a role is the outcome the b2b campaign is
             buying; optimising on registration alone rewards an account
             that never posts anything. */
          metaTrackCustom("JobPosted", signupAudience("organization"));
          gaTrack("first_posting_created", { poster_type: "org" });
          trackEvent("first_posting_created", {
            poster_type: "org", account_type: "organization", flow: "company_doors", count: 1,
          });
        } catch {
          trackEvent("posting_create_failed", {
            account_type: "organization", flow: "company_doors",
          });
          toast("Account created. Give your post one last look and save it.", { icon: "📝" });
        }
      }
      /* Nothing here should outlive the signup. Once the account exists the
         work is on the server, and what is left in storage is a name and an
         email address sitting on somebody's phone. */
      clearSignupProgress();
      clearCompanyDraft();
      setDone(true);
    } catch {
      /* A submit that fails for anything other than a clean field error used to
         record nothing at all. The visitor sees a toast and leaves; the funnel
         shows signup_submitted with no account_created and no reason, which is
         indistinguishable from somebody closing the tab mid-request. Six of 85
         submits in the week to 2026-09-06 ended this way, so it is worth about
         a signup a day and was invisible. */
      trackEvent("signup_request_failed", { account_type: "organization", flow: "company_minimal", stage: "register" });
      toast.error("Network error. Please try again.");
      setFailReason("The connection dropped before we could finish setting up your account.");
      setShareOpen(true);
      if (!shareEmail && emailVal) setShareEmail(emailVal);
    } finally {
      setSubmitting(false);
    }
  };

  const goDashboard = async () => {
    if (branding?.branded) {
      let dest = "/company/dashboard";
      try { dest = await resolvePostLoginPath(branding); } catch { /* keep fallback */ }
      return navigate(dest);
    }
    // Somebody who came through the "post what you need" door said what they
    // wanted before they had an account. Landing them on /home makes them go
    // and find it again, which is the loop this whole screen exists to break.
    // Straight to the composer instead; every other door keeps the hub.
    // Already posted during signup: send them to the list to see it live rather
    // than to an empty composer asking for the thing they just wrote.
    /* Pay the promise the button made.

       When people matched, the primary action on this screen reads "See who
       matches". It posted the role and then sent them to the postings list,
       which is their own writing back at them and not the thing they pressed
       for. With matches and a posting id, open that posting's matches instead;
       the drawer is already built, it just had no way in from a URL. */
    if (postedDraft && postedId && draftMatchCount > 0) {
      return navigate(`/company/postings?matches=${postedId}&from=doors`);
    }
    /* Posted, but nobody matches yet.

       The postings list is the wrong room for this. It is one row, theirs, with
       nothing to do to it and no answer to "so what happens now" -- and the
       honest answer is that the post works in the background while they get on
       with setting the company up. The hub is where that is true: it says the
       post is live, offers the one link to it, and puts the rest of the product
       in front of somebody who has just arrived and has not seen any of it.

       `posted=1` is the flag and `post=<id>` the deep link, kept apart on
       purpose: the id is not guaranteed (the create can answer without one) and
       folding them into a single param makes a missing id indistinguishable
       from posting #1. */
    if (postedDraft) {
      const q = postedId ? `?posted=1&post=${postedId}` : "?posted=1";
      return navigate(`/home${q}`);
    }
    if (intent === "post") return navigate("/company/postings?new=1&from=doors");
    // Came for clients: the storefront is how clients find them.
    if (ask?.intent === "find_clients") return navigate("/company/storefront");
    return navigate("/home");
  };

  const savePassword = async () => {
    if ((pwd || "").length < 8) return showFieldError("password", "Password must be at least 8 characters.");
    setSettingPwd(true);
    try {
      await fetchWithAuth("/api/accounts/set-password/", { method: "POST", json: { password: pwd } });
      setPwdSet(true);
      setPwd("");
      trackEvent("password_set", { account_type: "organization", flow: "company_minimal" });
      toast.success("Password set. You can log in anytime.");
    } catch (e) {
      // On the field, where it stays until they type (WCAG 3.3.1).
      showFieldError("password", e?.data?.password?.[0] || "Could not set password. Please try again.");
    } finally {
      setSettingPwd(false);
    }
    return undefined;
  };

  /* Enter moves to the next field. It used to submit the whole form from any
     of the three.

     On a phone the return key is labelled Go, Next or Done depending on the
     keyboard, and people press it to advance. So somebody typed their name,
     pressed it, and was told off about an email address they had not reached
     yet. The clickstream has one: a validation block on `email` with
     `value_length: 0` and no click event anywhere near it, because no button
     was pressed. They recovered, filled it in, and were then refused twice
     more for a different reason.

     Only the last field submits, which is what the key means there. */
  const focusNext = (id) => {
    const el = document.getElementById(id);
    if (el) el.focus();
  };
  const onKeyDown = (nextFieldId) => (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (nextFieldId) return focusNext(nextFieldId);
    if (!submitting) createAccount();
  };

  const inputClass =
    "w-full h-12 px-4 bg-surface text-ink placeholder-faint border border-line-control rounded-lg [color-scheme:light] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors";
  const label = "block text-sm font-medium text-secondary mb-1.5";

  if (PEEK_ENABLED && !peeked && !done) {
    return (
      <TalentPeek
        onContinue={(via) => {
          trackEvent("talent_peek_continue", {
            via, account_type: "organization", flow: "company_minimal",
          });
          markPeekSeen();
          setPeeked(true);
        }}
      />
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-4xl mb-2 text-center">🎉</div>
        <h1 className="font-display italic text-ink text-2xl text-center">
          {firstName ? `You're in, ${firstName}.` : "You're in."}
        </h1>
        <p className="mt-2 text-secondary text-sm text-center">
          {orgName.trim()} is set up. We're sending you a short guide on getting your team running.
        </p>

        {/* No "verify your email" box: the step is off
            (EMAIL_VERIFICATION_ENABLED) and pointing somebody at an inbox
            nothing was sent to is worse than saying nothing at all. */}

        {/* Passwordless account: a password now is what lets them come back on
            another device without hunting for the claim email. */}
        <div className="mt-3 border border-line rounded-xl p-4">
          {pwdSet ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="h-6 w-6 rounded-full bg-brand text-white flex items-center justify-center text-xs">✓</span>
              <span className="text-ink font-medium">Password set</span>
              <span className="text-secondary">You can log in anytime.</span>
            </div>
          ) : (
            <>
              <label htmlFor="gc-password" className="block text-ink text-sm font-medium">Create a password</label>
              <p className="text-secondary text-sm mt-0.5">So you can log back in on any device.</p>
              <div className="flex gap-2 mt-3">
                <input
                  id="gc-password"
                  {...fieldProps("password")}
                  type="password"
                  value={pwd}
                  onChange={(e) => { setPwd(e.target.value); clearError("password"); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!settingPwd) savePassword(); } }}
                  placeholder="Min. 8 characters"
                  className={inputClass + " flex-1"}
                />
                <button
                  onClick={savePassword}
                  disabled={settingPwd}
                  className="px-4 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-50 whitespace-nowrap"
                >
                  {settingPwd ? "Saving…" : "Set"}
                </button>
              </div>
              <FieldError field="password" errors={errors} />
            </>
          )}
        </div>

        <button onClick={goDashboard} className={btnPrimary + " mt-6"}>
          Go to my dashboard
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* A way back. Every screen before this one has one, and this one had
          nothing but the browser's back button -- which in the native app is a
          swipe some people do not know about, and which on the web reloads the
          whole get-started route and loses the draft. */}
      {/* Top-left corner of the card, like every other signup screen (Jake,
          2026-10-04). One step back, never out of the flow. */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="absolute top-4 left-4 inline-flex items-center gap-1 min-h-[36px] px-1 text-xs text-secondary hover:text-ink"
        >
          <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
        </button>
      )}
      <StepProgress step={step} total={totalSteps} />
      {/* The founding deal leads the screen (Jake, 2026-10-04): it is the
          reason to sign up today, so it is the first thing read, not the
          last thing under the button. */}
      {/* Their place in the line, not a count of what is left. A number you
          would hold reads as yours; a number remaining reads as inventory.
          "You'd be" rather than "you are", because the position is only theirs
          once the account exists and somebody else may take it first. */}
      {/* What the place is actually worth, itemised.
          Every line is checkable in the code rather than written for effect:
          the badge is FoundingOrgBadge, already rendering on posting and
          service cards; the waiver has no end date (founding_org_end is null);
          the rate is FOUNDING_ORG_FEE_BPS, 400, and 500 when a partner
          referred them, which is why it is read off the branding rather than
          printed as a flat 4% for everyone. */}
      {/* The 100 are taken: this replaces the founding card (Jake, 2026-10-04). */}
      {founding && founding.left <= 0 && (
        <div className="mb-4 rounded-xl border border-brand/25 bg-brand-tint/50 px-3.5 py-3">
          <p className="text-xs font-semibold leading-snug text-brand-ink">Your subscription is waived for three months.</p>
          <p className="mt-1 text-[0.71875rem] leading-snug text-brand-ink">A 10% fee on contract payments. No card needed.</p>
        </div>
      )}
      {founding?.left > 0 && (
        <div className="mb-4 rounded-xl border border-brand/25 bg-brand-tint/50 px-3.5 py-3">
          <p className="text-xs font-semibold leading-snug text-brand-ink">
            You'd be founding company #{founding.taken + 1} of 100.
            <span className="ml-1 font-normal opacity-80">
              {founding.left} {founding.left === 1 ? "place" : "places"} left.
            </span>
          </p>
          <ul className="mt-2 grid gap-1">
            {[
              "Subscription fee waived, permanently",
              `${foundingFeePct}% on contract payments, not 10%`,
              "Post as much as you like, free, forever",
              "A founding badge on everything you post",
            ].map((line) => (
              <li key={line} className="flex items-start gap-1.5 text-[0.71875rem] leading-snug text-brand-ink">
                <Check size={13} strokeWidth={2.6} className="mt-[1px] shrink-0" aria-hidden="true" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Name the people, not the post.
          "Great! Unlock your free post." named the wrong reward. The screen
          before it shows blurred faces and says names unlock when your account
          exists, and then this one changed the subject to the thing they wrote
          themselves -- and put a gate word on it, so Sift reads as the party
          withholding their own work. It also said "Great!" second, directly
          after the composer said it first.

          The count comes from the composer, which had it all along and passed
          on the draft alone. When it is zero, or when they skipped the
          composer, the headline falls back to the post: we never print a
          number we do not have. */}
      {draftMatchCount > 0 ? (
        <h1 className="font-display italic text-ink text-2xl sm:text-3xl">
          {draftMatchCount} {draftMatchCount === 1 ? "person is" : "people are"} waiting on this post.
        </h1>
      ) : (
        <h1 className="font-display italic text-ink text-2xl sm:text-3xl">
          {mirrorHeadline(ask, draft)}
        </h1>
      )}
      {draftMatchCount > 0 && (
        <p className="mt-2 text-sm text-secondary">Add your details and we'll put you in touch.</p>
      )}
      {/* Their own words, quoted back. The draft is finished work and this is
          the only screen that does not show it, which made the form feel like
          a fresh start rather than the last step of one. */}
      {/* From CompanyAsk the headline already answers them; no quote. */}
      {draft?.title && !ask && (
        <p className="mt-3 rounded-xl border border-brand/25 bg-brand-tint/50 px-3.5 py-2.5
                      text-[0.8125rem] font-medium leading-snug text-brand-ink">
          “{draft.title}”
        </p>
      )}
      {/* One-tap, on the screen where companies stop.
          Over fourteen days 28 companies reached this form and 3 finished: 25
          submitted nothing at all -- no validation blocks, no server refusals,
          nothing. The closing band on the landing page had been offering
          Facebook, Google and Microsoft the whole time, so the site promised one
          tap and then produced a password field.

          accountType is "organization", and that is what makes these count:
          SocialAuthButtons fires account_created, GA4 sign_up and Meta
          CompleteRegistration with that audience attached, so an OAuth company
          lands in the funnel beside an email one instead of in the unknown
          column.

          The organisation is still named after this. OAuth gives us a person,
          not a company, and finish-setup collects the rest -- the same order the
          individual side already uses.

          Moved above the fields on 2026-09-14. One tap is less work than three
          fields, so it goes first and the typing is the fallback, not the other
          way round. The divider that used to sit above these buttons said "or
          use socials", which only made sense when they came second. */}
      {/* Nothing to offer somebody who is already signed in -- they got here
          by pressing one of these. */}
      {!alreadySignedIn && (
      <div className="mt-5">
        <SocialAuthButtons
          verb="Sign up"
          accountType="organization"
          disabled={submitting}
          onAuthed={async (data) => {
            // account_created and the pixel events already fired inside
            // SocialAuthButtons, the only place that knows the provider.
            // Firing them here too would double-count every social signup.
            if (data?.is_new_user) {
              trackEvent("verification_completed", {
                method: "oauth", account_type: "organization",
              });
            }
            toast.success("You're in: welcome to Sift!");
            await goDashboard();
          }}
        />
      </div>

      )}

      {!alreadySignedIn && (
      <div className="mt-5 flex items-center gap-3">
        <span className="h-px flex-1 bg-line" />
        <span className="text-xs text-tertiary">or use your email</span>
        <span className="h-px flex-1 bg-line" />
      </div>
      )}

      {/* "No card" is gone. It was the only mention of payment anywhere in
          this funnel, and raising it on a free signup is the first time the
          reader has thought about a card at all: reassurance that plants the
          worry it answers.

          "We'll ask for the rest in a moment" went the same way. It promised
          more forms to somebody looking at one field, and now there is no
          "rest" to ask for: all three are on the screen and every one is
          labelled. Nothing needs saying here at all. */}

      {/* Not asked of somebody who signed up with one tap. We already have
          their email and their name from the provider, and asking again for
          something we hold is how a thirty-second signup turns back into a
          form. Only the company is still missing. */}
      {!alreadySignedIn && (
        <>
      <label className={label + " mt-6"} htmlFor="gc-email">Email</label>
      {/* No autoFocus. It opened the keyboard on arrival and scrolled the
          postings above straight off the screen, which is the one thing on
          this page arguing that anybody is here. */}
      <input id="gc-email" {...fieldProps("email")} type="email"
             value={email} onChange={(e) => { adoptAutofill(); setEmail(e.target.value); clearError("email"); clearNeed("email"); }}
             onKeyDown={onKeyDown(fullForm ? "gc-name" : null)} enterKeyHint={fullForm ? "next" : "go"} placeholder="you@company.com"
             autoComplete="email" inputMode="email" autoCapitalize="none"
             autoCorrect="off" spellCheck="false" className={inputClass + needRing("email")} />
      <FieldError field="email" errors={errors} />

      {fullForm && (
        <>
      <label className={label + " mt-4"} htmlFor="gc-name">Your name</label>
      <input id="gc-name" {...fieldProps("name")}
             value={name} onChange={(e) => { adoptAutofill(); setName(e.target.value); clearError("name"); clearNeed("name"); }}
             onKeyDown={onKeyDown("gc-org")} enterKeyHint="next" placeholder="Your name"
             autoComplete="name" className={inputClass + needRing("name")} />
      <FieldError field="name" errors={errors} />
        </>
      )}
        </>
      )}

      {/* In email_only, shown only when there is nothing to work the company out
          from: a one-tap signup, or an address that gives neither. */}
      {(alreadySignedIn || fullForm || errors.orgName) && (
        <>
      <label className={label + " mt-4"} htmlFor="gc-org">Company</label>
      <input id="gc-org" {...fieldProps("orgName")}
             value={orgName} onChange={(e) => { adoptAutofill(); setOrgName(e.target.value); clearError("orgName"); clearNeed("orgName"); }}
             onKeyDown={onKeyDown(null)} enterKeyHint="go" placeholder="Company name"
             autoComplete="organization" className={inputClass + needRing("orgName")} />
      <FieldError field="orgName" errors={errors} />
        </>
      )}

      {/* Looks inactive until the form is ready, but stays tappable.

         A genuinely `disabled` button is how people ended up here. It gives no
         feedback at all: you press it, nothing happens, you press again, you leave.
         That is the same dead end as the six refusals one visitor collected on
         2026-08-14, just quieter. So the grey sets the expectation and the tap still
         does something useful -- it puts the cursor in the first thing that is
         missing and opens the keyboard.
      
         The state is carried by the styling alone, deliberately: `aria-disabled`
         was the first attempt and it undoes the whole point. It announces the
         control as unavailable, and tooling that honours it refuses to activate
         the button at all -- Playwright rejects a click on one outright. A
         button we want people to press must not tell anybody it cannot be
         pressed. What it does on press is the affordance, and everyone gets the
         same one. */}
      <button
        onClick={createAccount}
        disabled={submitting}
        className={(formReady ? btnPrimary : btnPrimaryIdle) + " mt-7"}
      >
        {/* Plain where there is nothing to show them. A label promising what
            happens to the post reads as a bigger commitment than the three
            fields above it; "Sign up" is the thing they already know how to
            agree to. Kept as "See who matches" when people actually matched,
            because then there IS something on the far side of the button and
            naming it is not a promise, it is the reason.

            If a post-shaped verb is ever wanted back here, it is "Make my post
            live". "Put" was tried and is retired. */}
        {submitting
          ? "Setting up…"
          : alreadySignedIn ? "Create my company"
          : draftMatchCount > 0 ? "See who matches" : "Sign up"}
      </button>

      {/* The two objections, answered where the hesitation is rather than on a
          landing page they scrolled past ten minutes ago.

          Both are literally true and neither is the line that was nearly
          shipped here. "No markup" and "free to hire" are false: Sift takes a
          platform fee on payments run through it (4% for a founding company,
          5% if a partner referred them). What is free is posting, and keeping
          somebody permanently -- no conversion fee, no buyout, ever -- which
          is the thing an agency charges most for and the actual difference.

          The founding place is real and automatic: maybe_assign_org_founder
          runs at organisation creation while fewer than 100 exist, and a
          founding org's waiver has no end date. The number is read live, so
          the offer and the sentence expire together. */}
      <p className="mt-3 text-center text-xs text-tertiary">
        Free to post. No fee to keep anyone you hire, ever.
      </p>
      {/* The smaller ask, for the person who cannot say yes on their own.
          Only offered when there is a draft to send: with nothing written,
          "email it to me" has no it. See ShareDraftAPIView for the reasoning
          and the abuse guards. */}
      {(draft?.title || failReason) && (
        <div ref={shareRef} className="mt-5 border-t border-line pt-4">
          {/* When something went wrong, say what, before asking for anything.
              A refusal used to end here: a red line on a field or a toast, and
              the visitor left with their draft still in our hands and their
              address not. Naming the problem is the honest half; offering to
              send them what they wrote is the half that keeps the thread. */}
          {failReason && shareState !== "sent" && (
            <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3">
              <p className="text-sm font-semibold text-amber-900">
                We couldn't finish setting up your account.
              </p>
              <p className="mt-1 text-[0.8125rem] leading-snug text-amber-900/90">{failReason}</p>
              <p className="mt-2 text-[0.8125rem] leading-snug text-amber-900/90">
                Your post isn't lost. Give us an address and we'll email it to you, and
                someone here will pick this up with you directly.
              </p>
            </div>
          )}

          {/* Said out loud, because they did not come looking for it.

              This is the one case where the rescue opened itself, so it has to
              explain why it is suddenly there. When there are matches it leads
              with the number: the post is not hypothetical, there are named
              people on the other side of it, and that is the fact most likely
              to be worth forwarding to whoever actually signs. */}
          {sharePromptReason && shareState !== "sent" && !failReason && (
            <div className="mb-3 rounded-xl border border-brand/30 bg-brand-tint/40 px-3.5 py-3">
              <p className="text-sm font-semibold text-ink">
                {draftMatchCount > 0
                  ? `${draftMatchCount} ${draftMatchCount === 1 ? "person" : "people"} already match this post.`
                  : "Your post is written and ready."}
              </p>
              <p className="mt-1 text-[0.8125rem] leading-snug text-secondary">
                {draftMatchCount > 0
                  ? "Don't lose it. We'll email it to you, or straight to whoever signs off, and they can pick it up from there."
                  : "We'll email it to you, or to whoever signs off, so nothing you wrote has to be written twice."}
              </p>
            </div>
          )}

          {shareState === "sent" ? (
            <p className="text-center text-sm text-secondary">
              Sent to <span className="font-medium text-ink">{shareEmail}</span>.
              {failReason
                ? " We have your post and your address, and someone here will be in touch."
                : " Forward it to whoever needs to sign off, and finish signing up whenever you're ready."}
            </p>
          ) : shareOpen ? (
            <div>
              <label className={label} htmlFor="gc-share">{failReason ? "Where should we send it?" : "Where should we send it?"}</label>
              <div className="mt-1.5 flex gap-2">
                <input
                  id="gc-share" type="email" value={shareEmail}
                  onChange={(e) => { setShareEmail(e.target.value); setShareState("idle"); }}
                  onKeyDown={(e) => { if (e.key === "Enter") shareDraft(); }}
                  placeholder="you@company.com" autoComplete="email" inputMode="email"
                  autoCapitalize="none" autoCorrect="off" spellCheck="false"
                  className={inputClass + " flex-1"}
                />
                <button
                  type="button" onClick={shareDraft} disabled={shareState === "sending"}
                  className="px-4 rounded-pill bg-sunken border border-line-strong text-ink
                             text-sm font-medium whitespace-nowrap disabled:opacity-50"
                >
                  {shareState === "sending" ? "Sending…" : "Send"}
                </button>
              </div>
              {shareState === "error" && (
                <p className="mt-1.5 text-xs font-medium text-red-700" role="alert">
                  That didn't send. Check the address and try again.
                </p>
              )}
              <p className="mt-2 text-xs text-tertiary">
                Nothing is posted and no account is created. It's your draft, in an email.
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setShareOpen(true);
                trackEvent("share_draft_opened", { account_type: "organization" });
              }}
              className="w-full flex items-center justify-center gap-2 text-sm text-secondary
                         hover:text-ink transition-colors"
            >
              <Mail size={15} strokeWidth={2} aria-hidden="true" />
              {/* The old copy asked a question and then answered it with the
                  wrong person. "Not the one who signs things?" is precisely the
                  case where emailing it to YOURSELF is not the useful offer --
                  the address that matters is the one belonging to whoever does
                  sign. Both are on the table now, and the box below takes any
                  address, so the sentence finally matches what the form does. */}
              Not the one who signs things? Email it to yourself, or to whoever is
            </button>
          )}
        </div>
      )}

      {/* No "Actually, I'm looking for work" here any more. This screen is
          reached by choosing a door on the company screen, which already
          carries the switch, and the Back button below returns to it -- so
          the escape hatch exists one tap away rather than as a fourth
          competing action on the form itself. */}

    </Shell>
  );
}

const btnPrimary =
  "w-full h-12 bg-brand text-white rounded-pill font-medium hover:bg-brand-hover active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed";

/* Same shape and size, visibly not ready. Muted rather than hidden or removed,
   because the button must stay exactly where the eye expects it -- moving or
   hiding the primary action is worse than greying it. */
const btnPrimaryIdle =
  "w-full h-12 bg-sunken text-tertiary border border-line-strong rounded-pill font-medium active:scale-95 transition-all";

function Shell({ children }) {
  return (
    <>
      {/* Deep bottom gutter on phones so the page can scroll the submit button
          clear of the keyboard. See the note in GetStartedNeedFirst's Shell.
          The `sm:pb-24` is for the ticker: on a desktop viewport the card is
          vertically centred and would otherwise sit under it. */}
      {/* The safe-area inset is added to the padding, not swapped for it. In the
          native app this screen can be the first thing after the splash, with no
          nav bar above it to clear the notch, so `pt-8` alone put the card under
          the status bar. CompanyDoors was fixed for this; this screen was not,
          which is why the form still sat too high on an iPhone. */}
      <div
        className="min-h-screen bg-bg flex items-start sm:items-center justify-center px-4 pb-[50vh] sm:pb-24"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 2.5rem)" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative w-full max-w-md bg-surface border border-line rounded-2xl shadow-sift-sm p-8"
        >
          <div className="flex justify-center mb-5">
            <SiftMark size={44} />
          </div>
          {children}
        </motion.div>
      </div>
      {/* The availability ticker was here, fixed to the bottom of the
          viewport. Removed: inside the hero overlay it sat across the foot of
          the panel and cut off mid-word, so the last thing somebody saw on the
          account form was a strip of clipped text. The carousel on the landing
          page already carries this evidence, in full, before anyone reaches
          this screen. */}
    </>
  );
}


/* Say back what they came for (Jake, 2026-10-04): the headline is the answer
   to the question they were asked on the first screen, not a generic
   "create your account". */
function mirrorHeadline(ask, draft) {
  if (draft?.title) {
    // "Electrician Needed" read as "your electrician Needed hired": drop the
    // ad filler, then lowercase Title Case words but keep acronyms (HVAC, CNA).
    const t = draft.title.split(/\s[-–|(]\s?/)[0].replace(/[.!]+$/, "")
      .replace(/^(now hiring|hiring|seeking|wanted|need(ed)?)[:\s-]+(an?\s+)?/i, "")
      .replace(/([\s,:-]+(needed|wanted|required|asap|immediately))+$/i, "")
      .trim()
      .split(/\s+/)
      .map((w) => (/^[A-Z][a-z]/.test(w) ? w.toLowerCase() : w))
      .join(" ");
    if (!t || t.length > 32) return "Let's get this role filled.";
    return `Let's get your ${t} hired.`;
  }
  if (ask?.intent === "manage") return "Let's get your people on Sift.";
  if (ask?.intent === "find_clients") return "Let's get you in front of clients.";
  if (ask?.intent === "info") return "Free to join. Look around first.";
  if (draft) return "Your post is ready to go live.";
  return "Free to join.";
}
